/** Prezentacja urzędowego numeru kolczyka: PL1000 + 8 cyfr. */

export const EAR_TAG_PREFIX = "PL1000";
export const EAR_TAG_EXAMPLE = "PL100022789706";

export const TAG_DISPLAY_FORMATS = ["plain", "groups", "tail_one", "tail_three"] as const;

export type TagDisplayFormat = (typeof TAG_DISPLAY_FORMATS)[number];

export type TagDisplayPreferences = {
  display_format: TagDisplayFormat;
  bold_last_digit: boolean;
};

export const DEFAULT_TAG_DISPLAY: TagDisplayPreferences = {
  display_format: "plain",
  bold_last_digit: false,
};

/** Etykiety wariantów zgodne z układem znaków (X = cyfra po prefiksie). */
export const TAG_DISPLAY_FORMAT_LABELS: Record<TagDisplayFormat, string> = {
  plain: "PL1000XXXXXXXX",
  groups: "PL1000 XXX XXXX X",
  tail_one: "PL1000XXXXXXX X",
  tail_three: "PL1000XXXXX XXX",
};

export function isTagDisplayFormat(value: unknown): value is TagDisplayFormat {
  return typeof value === "string" && (TAG_DISPLAY_FORMATS as readonly string[]).includes(value);
}

export function normalizeTagDisplayPreferences(value: unknown): TagDisplayPreferences {
  if (!value || typeof value !== "object") {
    return DEFAULT_TAG_DISPLAY;
  }
  const record = value as { display_format?: unknown; bold_last_digit?: unknown };
  return {
    display_format: isTagDisplayFormat(record.display_format)
      ? record.display_format
      : DEFAULT_TAG_DISPLAY.display_format,
    bold_last_digit: Boolean(record.bold_last_digit),
  };
}

/** Zwarty numer PL1000 + 8 cyfr albo null, gdy to nie jest taki kolczyk. */
export function matchOfficialEarTag(value: string): string | null {
  const compact = value.toUpperCase().replace(/[\s-]/g, "");
  if (!compact.startsWith(EAR_TAG_PREFIX)) {
    return null;
  }
  const suffix = compact.slice(EAR_TAG_PREFIX.length);
  if (!/^\d{8}$/.test(suffix)) {
    return null;
  }
  return compact;
}

export function formatEarTag(value: string, format: TagDisplayFormat): string {
  const compact = matchOfficialEarTag(value);
  if (!compact) {
    return value;
  }
  const suffix = compact.slice(EAR_TAG_PREFIX.length);
  switch (format) {
    case "groups":
      return `${EAR_TAG_PREFIX} ${suffix.slice(0, 3)} ${suffix.slice(3, 7)} ${suffix.slice(7)}`;
    case "tail_one":
      return `${EAR_TAG_PREFIX}${suffix.slice(0, 7)} ${suffix.slice(7)}`;
    case "tail_three":
      return `${EAR_TAG_PREFIX}${suffix.slice(0, 5)} ${suffix.slice(5)}`;
    case "plain":
      return compact;
    default:
      return compact;
  }
}
